
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useBuyerAuth } from "@/hooks/useBuyerAuth";
import { TwoFactorVerificationDialog } from "@/components/auth/TwoFactorVerificationDialog";
import { getRememberedEmail, saveRememberedEmail, clearRememberedEmail, getRememberMePreference, clearAllAuthStorage } from "@/utils/authStorage";
import { isRestrictedBrowserContext } from "@/utils/browserDetection";
import { supabase } from "@/integrations/supabase/client";

const BuyerLoginPage = () => {
  const [email, setEmail] = useState(getRememberedEmail('buyer'));
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(getRememberMePreference('buyer'));
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const { signInAsBuyer, signOutAsBuyer, complete2FAVerification, isLoading, pending2FA, pending2FAUserId, pending2FAMethod } = useBuyerAuth();
  
  // Clean up stale auth data in restricted browser contexts (WebViews, Chrome Custom Tabs)
  // This prevents the "Signing In..." hang that occurs due to isolated storage
  useEffect(() => {
    const cleanupStaleAuth = async () => {
      if (isRestrictedBrowserContext()) {
        console.log('Restricted browser context detected (WebView/Chrome Custom Tab), clearing stale auth data');
        clearAllAuthStorage();
        await supabase.auth.signOut().catch(() => {
          // Ignore errors - we just want to ensure clean state
        });
      }
    };
    cleanupStaleAuth();
  }, []);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRateLimitError(null);
    
    // Validate inputs
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      await signInAsBuyer(email, password, rememberMe);
      // Save or clear remembered email based on checkbox
      if (rememberMe) {
        saveRememberedEmail(email, 'buyer');
      } else {
        clearRememberedEmail('buyer');
      }
    } catch (error: any) {
      if (error.message && error.message.includes('Too many attempts')) {
        setRateLimitError(error.message);
      } else {
        setError(error.message || 'Sign in failed. Please try again.');
      }
    }
  };
  
  return (
    <>
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center text-[#ffd700]">
            Welcome Back, Investor
          </CardTitle>
          <CardDescription className="text-center text-gray-300">
            Sign in to find your next investment property
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {rateLimitError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{rateLimitError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/buyer/forgot-password"
                  className="text-sm text-[#ffd700] hover:text-[#e6c200]"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white"
                autoComplete="current-password"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(!!checked)}
              />
              <Label htmlFor="remember" className="text-sm font-normal">
                Remember me
              </Label>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] py-3 text-lg font-semibold"
              disabled={isLoading}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </Button>
            
            <div className="text-center">
              <span className="text-sm text-gray-400">
                Don't have an account?{" "}
                <Link to="/buyer/register" className="text-[#ffd700] hover:text-[#e6c200]">
                  Sign up here
                </Link>
              </span>
            </div>
            
            <div className="text-center mt-2">
              <span className="text-sm text-gray-400">
                Are you a seller?{" "}
                <Link to="/seller/login" className="text-[#ffd700] hover:text-[#e6c200]">
                  Login here
                </Link>
              </span>
            </div>
          </form>
        </CardContent>
      </Card>

      <TwoFactorVerificationDialog
        open={pending2FA}
        userId={pending2FAUserId || ''}
        onVerified={complete2FAVerification}
        onCancel={async () => {
          await signOutAsBuyer();
        }}
      />
    </>
  );
};

export default BuyerLoginPage;
